// src/pages/Home/OpenPositions.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import JobPreview from "./JobPreview";
import { formatDateOnly } from "../../utils/formatDateOnly";
import "../../styles/Form.css";
import "../../styles/OpenPositions.css";

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      "...",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

export default function OpenPositions() {
  const topRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showPanel, setShowPanel] = useState(false);
  const [selected, setSelected] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // centralize scroll locking here
  useEffect(() => {
    document.body.style.overflow = showPanel ? "hidden" : "";
    return () => {
      // restore on unmount (e.g., navigating away)
      document.body.style.overflow = "";
    };
  }, [showPanel]);

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${BASE_URL}/api/open-positions`, {
          headers,
        });
        if (!off) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!off)
          setErr(
            e?.response?.data?.message ||
              e.message ||
              "Failed to load open positions"
          );
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => {
      off = true;
    };
  }, [BASE_URL]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const title = (p.p_position_title || "").toLowerCase();
      const number = String(p.p_position_number || "");

      const orgUnit = (p.ou_name || p.p_org_unit || "")
        .toString()
        .toLowerCase();
      const typeName = (p.pt_name || p.p_position_type || "")
        .toString()
        .toLowerCase();

      const location = (
        p.location_city ||
        p?.p_contact_info?.city ||
        p?.p_posting?.p_location ||
        ""
      )?.toLowerCase();

      return (
        title.includes(q) ||
        number.includes(q) ||
        orgUnit.includes(q) ||
        typeName.includes(q) ||
        location.includes(q)
      );
    });
  }, [items, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const cityA = (
        a.location_city ||
        a?.p_contact_info?.city ||
        a?.p_posting?.p_location ||
        ""
      )
        .toString()
        .trim();
      const cityB = (
        b.location_city ||
        b?.p_contact_info?.city ||
        b?.p_posting?.p_location ||
        ""
      )
        .toString()
        .trim();

      const provinceA = (a.p_province || a?.p_contact_info?.province || "")
        .toString()
        .trim();
      const provinceB = (b.p_province || b?.p_contact_info?.province || "")
        .toString()
        .trim();

      const locationCompare =
        provinceA.localeCompare(provinceB, undefined, {
          sensitivity: "base",
        }) ||
        cityA.localeCompare(cityB, undefined, {
          sensitivity: "base",
        });

      if (locationCompare !== 0) return locationCompare;

      const numA = Number(a.p_position_number) || 0;
      const numB = Number(b.p_position_number) || 0;
      if (numA !== numB) return numB - numA;

      return (a.p_position_title || "").localeCompare(
        b.p_position_title || "",
        undefined,
        { sensitivity: "base" }
      );
    });
  }, [filtered]);

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginationItems = useMemo(
    () => getPaginationItems(page, pages),
    [page, pages]
  );
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page]);

  const groupedByLocation = useMemo(() => {
    const groups = {};
    paged.forEach((p) => {
      const city =
        p.location_city ||
        p?.p_contact_info?.city ||
        p?.p_posting?.p_location ||
        "—";
      const province = p.p_province || p?.p_contact_info?.province || "—";

      const key = `${city}, ${province}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return groups;
  }, [paged]);

  useEffect(() => {
    if (page > pages) setPage(1);
  }, [pages, page]);

  const openDetails = (rec) => {
    setSelected(rec);
    setShowPanel(true);
    document.body.style.overflow = "hidden";
  };

  const closeDetails = () => {
    setShowPanel(false);
    setSelected(null);
    document.body.style.overflow = "";
  };

  const goToPage = (nextPage) => {
    setPage((currentPage) => {
      const pageNumber =
        typeof nextPage === "function" ? nextPage(currentPage) : nextPage;
      return Math.min(pages, Math.max(1, pageNumber));
    });

    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  if (loading) return <div className="form-wrapper my-4">Loading…</div>;
  if (err) return <div className="form-wrapper my-4 error">Error: {err}</div>;

  return (
    <div ref={topRef} className="form-wrapper my-3">
      <fieldset>
        <legend>Career Opportunities</legend>

        <div className="op-toolbar">
          <input
            className="op-search"
            type="text"
            placeholder="Search by number, name, type, location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="op-card-list">
          {Object.keys(groupedByLocation).length === 0 ? (
            <div className="muted">No results.</div>
          ) : (
            Object.entries(groupedByLocation).map(([loc, jobs]) => (
              <div key={loc} className="op-group">
                {/* Group header */}
                <div className="op-group-header">{loc}</div>

                {/* Job cards */}
                {jobs.map((p) => (
                  <div key={p.p_id} className="op-card">
                    <div className="op-card-header">
                      <h3 className="op-card-title">
                        {p.p_position_title || "—"}
                      </h3>
                      <button
                        className="btn-apply"
                        onClick={() => openDetails(p)}
                      >
                        Details
                      </button>
                    </div>
                    <div className="op-card-body">
                      <p>
                        <strong>📌 Position #:</strong>{" "}
                        {p.p_position_number || "—"}
                      </p>

                      {p.p_visible_info?.openings && (
                        <p>
                          <strong>👥 Openings:</strong>{" "}
                          {p.p_openings ?? p.p_posting?.p_num_openings ?? "—"}
                        </p>
                      )}

                      {p.p_visible_info?.work_type && (
                        <p>
                          <strong>🛠 Type:</strong>{" "}
                          {[
                            p.p_landing_page_sell?.position_type,
                            p.p_landing_page_sell?.driver_type,
                          ]
                            .filter(Boolean) // remove null/empty
                            .join(" - ") || "—"}
                        </p>
                      )}

                      {p.p_visible_info?.closing_date && (
                        <p>
                          <strong>📅 Closing:</strong>{" "}
                          {formatDateOnly(
                            p.p_closing_date ?? p.p_posting?.p_closing_date
                          )}
                        </p>
                      )}

                      {p.p_visible_info?.dangerous_goods && (
                        <p>
                          <strong>☣️ Dangerous Goods:</strong>{" "}
                          {p.p_dangerous_goods ??
                            p.p_posting?.p_dangerous_goods ??
                            "—"}
                        </p>
                      )}

                      {p.p_visible_info?.license_classes && (
                        <p>
                          <strong>🚛 License Classes:</strong>{" "}
                          {Array.isArray(p.p_landing_page_sell?.license_class)
                            ? p.p_landing_page_sell.license_class.join(", ")
                            : p.p_landing_page_sell?.license_class || "—"}
                        </p>
                      )}

                      {p.p_visible_info?.license_conditions && (
                        <p>
                          <strong>⚖️ License Conditions:</strong>{" "}
                          {Array.isArray(
                            p.p_landing_page_sell?.license_conditions
                          )
                            ? p.p_landing_page_sell.license_conditions.join(
                                ", "
                              )
                            : p.p_landing_page_sell?.license_conditions || "—"}
                        </p>
                      )}

                      {p.p_visible_info?.route && (
                        <p>
                          <strong>🗺️ Route:</strong>{" "}
                          {p.p_route_description ?? p.p_posting?.p_route ?? "—"}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="op-pagination">
          <div className="op-page-info">
            Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, filtered.length)} of {filtered.length}{" "}
            entries
          </div>
          <div className="op-page-buttons">
            <button
              className="op-page-button op-page-arrow"
              disabled={page <= 1}
              onClick={() => goToPage((p) => p - 1)}
            >
              «
            </button>
            {paginationItems.map((item, index) =>
              item === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="op-page-ellipsis"
                  aria-hidden="true"
                >
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  className={`op-page-button op-page-number ${
                    item === page ? "op-page-current" : ""
                  }`}
                  aria-current={item === page ? "page" : undefined}
                  onClick={() => goToPage(item)}
                >
                  {item}
                </button>
              )
            )}
            <button
              className="op-page-button op-page-arrow"
              disabled={page >= pages}
              onClick={() => goToPage((p) => p + 1)}
            >
              »
            </button>
          </div>
        </div>
      </fieldset>

      {/* Left slide details */}
      <div
        className={`left-slide-overlay ${showPanel ? "show" : ""}`}
        onClick={closeDetails}
      />
      <aside className={`left-slide-panel ${showPanel ? "show" : ""}`}>
        <div className="left-slide-header">
          <span className="left-slide-title">
            {selected?.p_position_title || "Position Details"}
          </span>
          <button
            className="left-slide-close"
            onClick={closeDetails}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="left-slide-content">
          <JobPreview
            job={selected}
            onApply={() => {
              const id = selected?.p_id;
              // Close the panel first (optional)
              setShowPanel(false);
              setSelected(null);
              // Then navigate
              const token = localStorage.getItem("token");
              if (!token) {
                navigate(`/register?redirect=/apply/${id}`);
              } else {
                navigate(`/apply/${id}`);
              }
            }}
          />
        </div>
      </aside>
    </div>
  );
}
